import React, { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Button, Modal } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { extendSessionExpiry, logoutUser } from '../auth/auth-slice';
import { FormattedMessage } from 'react-intl';

const DEFAULT_SESSION_TIMEOUT = 15 * 60 * 1000;
const DEFAULT_PROMPT_BEFORE_TIMEOUT = 5 * 60 * 1000;

export default function IdleSessionNotifier() {
  const timeout = window.config.sessionTimeout || DEFAULT_SESSION_TIMEOUT;
  const promptBeforeIdle = window.config.promptBeforeIdle || DEFAULT_PROMPT_BEFORE_TIMEOUT;

  const dispatch = useDispatch();
  const [messageId, setMessageId] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(-1);
  const [remainingMinutes, setRemainingMinutes] = useState(-1);
  const [promptOpen, setPromptOpen] = useState(false);
  const itemAssignmentCode = useSelector((state) => state.auth?.user?.itemAssignmentCode);
  const emailAuthenticated = useSelector((state) => state.auth?.user?.emailAuthenticated);
  const sessionExpired = useSelector((state) => state.auth?.user?.sessionExpired);

  function logoutAndRedirect() {
    if (!emailAuthenticated) {
      return;
    }

    dispatch(logoutUser());

    const redirectUrl = '/' + itemAssignmentCode;
    window.location.href = redirectUrl;
  }

  const onActive = () => {
    dispatch(extendSessionExpiry());
  };

  const onPrompt = () => {
    setPromptOpen(true);
  };

  const { getRemainingTime, activate, pause, resume } = useIdleTimer({
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  // Open the modal immediately when a server-side 401 sets sessionExpired.
  // Also pause the idle timer so it can't fire concurrently.
  useEffect(() => {
    if (sessionExpired) {
      pause();
      setMessageId('session.idleLoggedOut');
      setRemainingSeconds(0);
      setRemainingMinutes(0);
      setPromptOpen(true);
    }
  }, [sessionExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!emailAuthenticated || sessionExpired) {
      pause();
      return;
    }
    resume();
    const interval = setInterval(() => {
      const remainingMilliseconds = getRemainingTime();
      const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);
      const remainingMinutes = Math.ceil(remainingSeconds / 60.0);

      if (remainingSeconds > 0) {
        setMessageId('session.idleLogoutInSeconds');
      }

      if (remainingSeconds > 60) {
        setMessageId('session.idleLogoutInMinutes');
      }

      if (remainingSeconds === 0) {
        setMessageId('session.idleLoggedOut');
        clearInterval(interval);
      }

      setRemainingSeconds(remainingSeconds);
      setRemainingMinutes(remainingMinutes);
    }, 500);

    return () => clearInterval(interval);
  });

  function onOKClick() {
    if (remainingSeconds === 0) {
      logoutAndRedirect();
    }
    activate();
    setPromptOpen(false);
  }

  return (
    <Modal className="idle-session-notifier" size="mini" open={promptOpen && !!messageId}>
      <Modal.Header>
        <FormattedMessage id="session.idleLogoutPromptTitle" />
      </Modal.Header>
      <Modal.Content>
        <p className="justified">
          <FormattedMessage id={messageId} values={{ remainingSeconds, remainingMinutes }} />
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button className="green" onClick={() => onOKClick()} positive>
          <FormattedMessage id="common.ok" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
